import ViewManager from './ViewManager';
import BaseView from './BaseView';
import RenderMode from './RenderMode';
import AccessibilityFocusChangeEvent from './accessibility/AccessibilityFocusChangeEvent';
import KeyCode from './input/KeyCode';
import NarratorHelper from './accessibility/NarratorHelper';
import GestureHelper from './gestures/GestureHelper';
import TooltipManager from './ui/tooltip/TooltipManager';
import * as KeyHelper from './gestures/KeyHelper';
import * as ThemeLoader from './util/ThemeLoader';
// NOTE(mikcol): import from legacy
import './gestures/CursorHelper';
import './gestures/Gestures';
import ShowContextMenuEvent from './events/ShowContextMenuEvent';
import { addActiveVisualization, removeActiveVisualization } from './util/VisualizationUtil';
import * as ltjsEnvironment from './ltjsEnvironment';
import ResourceManager from './resources/ResourceManager';
import { getRgbaColorString } from './util/StyleUtils';
import ColorUtilities from './util/ColorUtilities';
import StyleElement from './styles/StyleElement';
import StyleAttribute from './styles/StyleAttribute';
import DimensionUnit from './util/DimensionUnit';
import RenderingType from './RenderingType';
import { getStackedWebmapDisplay } from './InjectedStackedWebmapDisplay';
function isTimePickResults(pickResults) {
    // we know that if we are to use the special time based tips the first item will be
    // a PickResult and its details with be a TimePickResultDetails
    if (!pickResults.length || !pickResults[0]) {
        return false;
    }
    var details = pickResults[0].getDetails();
    var TimePickResultDetails = ltjsEnvironment.moduleLookup('gtl::TimePickResultDetails');
    return TimePickResultDetails && details instanceof TimePickResultDetails;
}
var _activeViewTimeOutID = null;
var _activeViewId = '';
var visualContainerCounter = 0;
var _isE2EMode = null;
function _checkE2EMode() {
    var _a;
    if (_isE2EMode === null) {
        try {
            var searchParams = new URLSearchParams(window.location.search);
            _isE2EMode = ((_a = searchParams.get('VASDK_E2EMODE')) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === 'true';
        }
        catch (_) {
            _isE2EMode = false;
        }
    }
    return _isE2EMode;
}
function _isCanvasMode() {
    //
    // SVG is not allowed on a per-view basis, so as long as the global
    // mode is not SVG we are guaranteed that the view is a canvas.
    //
    return ViewManager.getGlobalRenderMode() !== RenderMode.SVG;
}
// could be a canvas or svg element.
function _getCanvasElement(containerId) {
    var canvasId = containerId + (_isCanvasMode() ? 'canvas' : 'svg');
    if (containerId) {
        return document.getElementById(canvasId);
    }
    return null;
}
function isStylesObject(styles) {
    return !!styles.getStyles;
}
var VisualizationManager = /** @class */ (function () {
    function VisualizationManager() {
        var _this = this;
        this._model = null;
        this._baseView = null;
        this._gestureHelper = null;
        this._narratorHelper = null;
        this._viewAdded = false;
        this._visContainerId = null;
        this._viewRendered = false;
        this._gestureOverrides = {};
        this._myActiveViewTimeoutID = null;
        this._tooltipsEnabled = true;
        this._showContextMenuEventListener = undefined;
        this._accessibilityFocusChangeListener = undefined;
        this._renderMode = RenderMode.AUTO;
        // TODO(mikcol): type params
        this._accessibilityActionCallback = null;
        this._showContextMenuCallback = null;
        this._customTooltipStyles = null;
        this._frameValidEventHandle = undefined;
        this._renderingTypeEventHandle = undefined;
        this._frameValidCallback = undefined;
        this._tooltipDataOverride = undefined;
        this._frameWidth = -1;
        this._frameHeight = -1;
        this._resizeObserver = null;
        this._restrictViewportGestures = false;
        this._zoomOverlayMessageDiv = null;
        this._zoomMessageTimerId = -1;
        this._stackedArcGISWebMapDiv = null;
        this._stackedWebmapDisplay = null;
        this._onBlur = function () {
            _this._hideTooltips();
        };
        this._onPointerHover = function (gesture) {
            if (!_this._tooltipsEnabled) {
                return;
            }
            //Could be a Styles object or an object bundle.
            var styles = _this._customTooltipStyles;
            if (styles && isStylesObject(styles)) {
                styles = styles.getStyles();
            }
            // In order to pick all of the data elements we pass in a secret -1, -1
            //   for width and height. Shh. (We have an agreement...)
            var pickResults = _this.getPickResultsInAperture({
                // NOTE(mikcol): legacy probably doesn't deal with the case that these are undefined?
                // NOTE(mikcol)| i think these are undefined when the one of event or element
                // NOTE(mikcol)| passed to GestureHelper.getMousePosition are falsy
                x: gesture.localX,
                y: gesture.localY,
                width: -1,
                height: -1,
            });
            var data;
            if (_this._tooltipDataOverride) {
                data = _this._tooltipDataOverride(pickResults, gesture);
            }
            if (data === undefined && pickResults) {
                if (isTimePickResults(pickResults)) {
                    TooltipManager.instance().setTimeTooltips(pickResults, _this._visContainerId, styles);
                    return;
                }
                else {
                    data = pickResults;
                }
            }
            TooltipManager.instance().setTooltips(data, _this._visContainerId, styles, {
                x: gesture.localX,
                y: gesture.localY,
            });
        };
        this._onMouseEnter = function () {
            if (_activeViewTimeOutID !== null) {
                clearTimeout(_activeViewTimeOutID);
                _activeViewTimeOutID = null;
                _this._myActiveViewTimeoutID = null;
            }
            if (_activeViewId !== _this._visContainerId) {
                _activeViewTimeOutID = window.setTimeout(function () {
                    _this._makeActiveView();
                }, 100);
                _this._myActiveViewTimeoutID = _activeViewTimeOutID;
            }
        };
        this._onMouseLeave = function () {
            if (_activeViewTimeOutID !== null) {
                clearTimeout(_activeViewTimeOutID);
                _activeViewTimeOutID = null;
                _this._myActiveViewTimeoutID = null;
            }
            if (_this._tooltipsEnabled) {
                TooltipManager.instance().hideTooltips(_this._visContainerId);
            }
        };
        this._onMouseWheel = function () {
            _this._removeZoomMessage();
        };
        this._onKeyDown = function (event) {
            if (!_this._baseView) {
                return;
            }
            var accessibilityController = _this._baseView.getAccessibilityController();
            if (!accessibilityController) {
                return;
            }
            var accessibilityElement = accessibilityController.getCurrentAccessibilityElement();
            if (!accessibilityElement) {
                return;
            }
            ///tooltip on accessibility
            if (KeyHelper.getPortableKeyCode(event) === KeyCode.PVK_SPACE) {
                var pickResult = accessibilityElement.getPickResult();
                if (pickResult && _this._visContainerId) {
                    TooltipManager.instance().setTooltips([pickResult], _this._visContainerId);
                }
                return;
            }
            //accessibilityActionCallback
            if (!_this._accessibilityActionCallback) {
                return;
            }
            if (KeyHelper.getPortableKeyCode(event) !== KeyCode.PVK_ENTER) {
                return;
            }
            _this._accessibilityActionCallback(accessibilityElement);
        };
        this.addGestureOverride('mouseEnter', this._onMouseEnter);
        this.addGestureOverride('mouseLeave', this._onMouseLeave);
        this.addGestureOverride('hover', this._onPointerHover);
        this.addGestureOverride('mouseWheel', this._onMouseWheel);
    }
    VisualizationManager.prototype.getStackedWebmapDisplay = function () {
        return this._stackedWebmapDisplay;
    };
    VisualizationManager.prototype.setStackedWebmapDisplay = function (value) {
        this._destroyStackedWebmapDisplay();
        this._stackedWebmapDisplay = value;
    };
    VisualizationManager.prototype.getTooltipsEnabled = function () {
        return this._tooltipsEnabled;
    };
    VisualizationManager.prototype.setTooltipsEnabled = function (value) {
        this._tooltipsEnabled = value;
        if (!value) {
            TooltipManager.instance().hideTooltips(this._visContainerId);
        }
    };
    VisualizationManager.prototype.getRestrictViewportGestures = function () {
        return this._restrictViewportGestures;
    };
    VisualizationManager.prototype.setRestrictViewportGestures = function (value) {
        this._restrictViewportGestures = value;
    };
    /**
     * Sets the render mode used by this view.
     * @param {sas.ltjs.commons.RenderMode} value the render mode. Valid values are AUTO,
     * CANVAS2D, and WEBGL.
     */
    VisualizationManager.prototype.setRenderMode = function (value) {
        if (this._renderMode === value) {
            return;
        }
        this._renderMode = value;
        // Reset the model to force recreation of the base view, which
        // will use the new render mode.
        var model = this.getModel();
        this.setModel(null);
        this.setModel(model, { applyAppTheme: false });
    };
    VisualizationManager.prototype.getRenderMode = function () {
        return this._renderMode;
    };
    VisualizationManager.prototype.getCustomTooltipStyles = function () {
        return this._customTooltipStyles;
    };
    /**
     * Custom style overrides for the tooltips that are rendered.
     * @param {(Style|Object.<string,string>)} value A sas.hc.ui.core.Styles or a map of css styles to override values.
     */
    VisualizationManager.prototype.setCustomTooltipStyles = function (value) {
        this._customTooltipStyles = value;
    };
    /**
     * The callback that gets called when the Enter key is released.
     * @param {Function} callback callback for the accessibility action handling.
     */
    VisualizationManager.prototype.setAccessibilityActionCallback = function (callback) {
        this._accessibilityActionCallback = callback;
    };
    /**
     * Returns a pickresult that describes what exists at the given x,y coordinate.
     * @param {int} x x coordinate
     * @param {int} y y coordinate
     * @param {boolean} isGlobal if the coordinates are global coordinates then this function will convert them to local coordinates.
     * @param {boolean} updateSelections Should the pick cause the data element at the given point to be selected
     * @returns {Array<PickResult>} description of what exists at the given x,y coordinate
     */
    VisualizationManager.prototype.getPickResults = function (x, y, isGlobal, updateSelections) {
        return this.getPickResultsInAperture({ x: x, y: y }, isGlobal, updateSelections);
    };
    VisualizationManager.prototype.getPickResultsInAperture = function (aperture, isGlobal, updateSelections) {
        var container = _getCanvasElement(this._visContainerId);
        if (!container) {
            return null;
        }
        aperture.width = aperture.width || 1;
        aperture.height = aperture.height || 1;
        var bounds = {
            width: container.clientWidth,
            height: container.clientHeight,
        };
        if (isGlobal) {
            var boundRect = container.getBoundingClientRect();
            aperture.x -= boundRect.left;
            aperture.y -= boundRect.top;
        }
        if (!bounds || aperture.x < 0 || aperture.y < 0 || aperture.x > bounds.width || aperture.y > bounds.height) {
            return null;
        }
        // portable has flipped coordinate space.
        aperture.y = Math.floor(bounds.height - aperture.y);
        var view = this.getBaseView();
        if (this.viewHasRendered() && view) {
            var apertureRect = [aperture.x, aperture.y, aperture.width, aperture.height];
            apertureRect[0] = view.rtlTransformXValue(apertureRect[0]);
            return view.getPickResultsInAperture(apertureRect, !!updateSelections);
        }
        return null;
    };
    VisualizationManager.prototype.setModel = function (model, optionsOrBaseView) {
        var _a, _b;
        // applyAppTheme defaults to true, unless the old function signature is used.
        // This form was used by VAViewerHTMLComponent's BIRDVisualization.js, where
        // the application theme should not be applied.
        var _c = optionsOrBaseView instanceof BaseView
            ? { applyAppTheme: false, baseView: optionsOrBaseView }
            : optionsOrBaseView !== null && optionsOrBaseView !== void 0 ? optionsOrBaseView : {}, _d = _c.applyAppTheme, applyAppTheme = _d === void 0 ? true : _d, baseView = _c.baseView;
        var oldModel = (_a = this._model) === null || _a === void 0 ? void 0 : _a.release();
        this._model = (_b = model === null || model === void 0 ? void 0 : model.retain()) !== null && _b !== void 0 ? _b : null;
        // tslint:disable-next-line:no-unsafe-any
        if (model) {
            // Apply application theme for graph and common component BaseViews
            var style = applyAppTheme ? ThemeLoader.getApplicationStyle() : model.getStyle();
            if (model.getStyle() !== style) {
                model.setStyle(style);
            }
            this.setZoomOverlayStylesFromStyle(style);
            // Use existing BaseView if the model hasn't changed
            if (!baseView && model.equals(oldModel)) {
                baseView = this.getBaseView();
            }
            if (!baseView) {
                // Graph models expect us to call createBaseView
                // Table models expect us to call createRenderer
                // but they implement both.
                // HACK: we can't depend on graph in Commons-HTML so we're
                // function checking an unrelated but graph specific API
                // tslint:disable-next-line:no-unsafe-any
                if (model.getInitialGraphState) {
                    // tslint:disable-next-line:no-unsafe-any
                    baseView = model.createBaseView();
                }
                else {
                    // tslint:disable-next-line:no-unsafe-any
                    baseView = model.createRenderer();
                }
            }
        }
        this.setBaseView(baseView);
    };
    VisualizationManager.prototype.getModel = function () {
        return this._model;
    };
    VisualizationManager.prototype.setBaseView = function (view) {
        // NOTE(mikcol): 'as BaseView' to preserve legacy behavior
        if (view && view.equals(this._baseView)) {
            return;
        }
        //clean up old base view.
        if (this._baseView) {
            this._removeViewFromViewManager();
            this._baseView.release();
            this._baseView = null;
        }
        if (_activeViewTimeOutID && _activeViewTimeOutID === this._myActiveViewTimeoutID) {
            clearTimeout(_activeViewTimeOutID);
            _activeViewTimeOutID = null;
            this._myActiveViewTimeoutID = null;
        }
        if (this._frameValidEventHandle) {
            this._frameValidEventHandle.detach();
            this._frameValidEventHandle = null;
        }
        this._destroyHelpers();
        this._unobserveElement();
        this._viewAdded = false;
        this._viewRendered = false;
        //add new base view.
        if (view) {
            this._baseView = view.retain();
            this.addCanvasToContainer();
        }
        // Reset the cached dimensions
        this._frameWidth = -1;
        this._frameHeight = -1;
    };
    VisualizationManager.prototype.getBaseView = function () {
        return this._baseView;
    };
    VisualizationManager.prototype.setElement = function (element) {
        this._unobserveElement();
        var parent = document.createElement('div');
        var id = 'visualContainer_' + String(visualContainerCounter++);
        parent.classList.add('visualContainer');
        parent.id = id;
        element.appendChild(parent);
        this.setContainerId(id);
    };
    VisualizationManager.prototype.getElement = function () {
        return this._visContainerId ? document.getElementById(this._visContainerId) : null;
    };
    VisualizationManager.prototype.setContainerId = function (id) {
        if (id === this._visContainerId) {
            return;
        }
        this._destroyHelpers();
        this._unobserveElement();
        //Make sure we don't leak canvases. This would be a weird case where the user
        //  is swapping out the element used. Probably a bad idea, but we should handle it.
        if (this._baseView) {
            this._removeViewFromViewManager();
            this._viewAdded = false;
            this._viewRendered = false;
        }
        this._visContainerId = id;
        this.addCanvasToContainer();
    };
    VisualizationManager.prototype.getContainerId = function () {
        return this._visContainerId;
    };
    VisualizationManager.prototype.setCanvasRole = function (role) {
        this._canvasRole = role;
    };
    VisualizationManager.prototype.setCanvasAriaLabel = function (label) {
        this._canvasAriaLabel = label;
    };
    VisualizationManager.prototype.addCanvasToContainer = function () {
        var _this = this;
        if (!this._visContainerId || !ltjsEnvironment.isInitialized()) {
            return;
        }
        if (!this._viewAdded) {
            this._addViewToViewManager();
        }
        if (_isCanvasMode()) {
            ViewManager.appendCanvasToContainer(this._visContainerId);
        }
        var canvasElement = _getCanvasElement(this._visContainerId);
        if (canvasElement) {
            canvasElement.style.position = 'absolute';
            if (this._canvasRole) {
                canvasElement.setAttribute('role', this._canvasRole);
            }
            if (this._canvasAriaLabel) {
                canvasElement.setAttribute('aria-label', this._canvasAriaLabel);
            }
        }
        var containerElement = document.getElementById(this._visContainerId);
        if (containerElement) {
            // the element can change between calls
            // to addCanvasToContainer
            if (this._resizeObserver) {
                this._resizeObserver.disconnect();
            }
            this._resizeObserver = new ResizeObserver(function (entries) {
                // this length should always be one
                if (!entries || entries.length < 1) {
                    return;
                }
                var entry = entries[0];
                if (_this._baseView && _this._viewAdded) {
                    // Canvas elements use integers for height and width
                    _this._frameWidth = Math.floor(entry.contentRect.width);
                    _this._frameHeight = Math.floor(entry.contentRect.height);
                }
            });
            this._applyZoomOverlayStyleOverrides();
            this._resizeObserver.observe(containerElement);
        }
        if (this._viewRendered) {
            this._initHelpersAndListeners();
        }
        if (_checkE2EMode()) {
            Object.defineProperty(canvasElement, '__a11yController', {
                get: function () { var _a; return (_a = _this.getBaseView()) === null || _a === void 0 ? void 0 : _a.getAccessibilityController(); },
            });
        }
    };
    VisualizationManager.prototype.viewHasRendered = function () {
        return this._viewRendered;
    };
    VisualizationManager.prototype._initHelpersAndListeners = function () {
        var _a, _b;
        this._destroyHelpers();
        if (!this._baseView || !this._visContainerId) {
            return;
        }
        var element = this.getElement();
        if (!element) {
            return;
        }
        this._gestureHelper = new GestureHelper(this._baseView, element, this._gestureOverrides, this._displayZoomOverlay.bind(this));
        this._narratorHelper = new NarratorHelper(this._baseView.getAccessibilityController(), element, this._visContainerId);
        element.addEventListener('keydown', this._onKeyDown);
        element.addEventListener('blur', this._onBlur);
        // tslint:disable-next-line:no-unsafe-any
        this._accessibilityFocusChangeListener = (_b = (_a = this._baseView.getAccessibilityController()) === null || _a === void 0 ? void 0 : _a.getModel()) === null || _b === void 0 ? void 0 : _b.attachEvent(AccessibilityFocusChangeEvent.ACCESSIBILITY_FOCUS_CHANGE, 
        // tslint:disable-next-line:no-unbound-method
        this._hideTooltips, this);
        if (!this._model) {
            return;
        }
        // tslint:disable-next-line:no-unsafe-any
        this._showContextMenuEventListener = this._model.attachEvent(ShowContextMenuEvent.SHOW_CONTEXT_MENU, 
        // tslint:disable-next-line:no-unbound-method
        this._handleShowContextMenuEvent, this);
        //S1544889: When some visualizations drill into an element, focus is lost at the portable level after the drill.
        //This forces the graph to have focus at the portable level if it has focus in the DOM. Bit of a hack.
        if (document.activeElement === element) {
            this._gestureHelper._onFocus();
        }
    };
    VisualizationManager.prototype.addGestureOverride = function (gestureName, callback) {
        if (!this._gestureOverrides) {
            throw new Error('Cannot add gesture override, overrides object is null! (VisualizationManager is probably destroyed)');
        }
        var gestureOverrides = this._gestureOverrides[gestureName];
        if (!gestureOverrides) {
            gestureOverrides = [];
            this._gestureOverrides[gestureName] = gestureOverrides;
        }
        gestureOverrides.push(callback);
    };
    VisualizationManager.prototype.hasGestureOverride = function (gestureName) {
        return !!this._gestureOverrides[gestureName];
    };
    VisualizationManager.prototype.getFrameValidCallback = function () {
        return this._frameValidCallback;
    };
    VisualizationManager.prototype.setFrameValidCallback = function (value) {
        this._frameValidCallback = value;
        if (!this._baseView) {
            return;
        }
        if (value && !this._frameValidEventHandle) {
            this._frameValidEventHandle = this._baseView.attachEvent(BaseView.FRAME_VALID, 
            // tslint:disable-next-line:no-unbound-method
            this._handleFrameValid, this);
        }
        else if (!value && this._frameValidEventHandle) {
            this._frameValidEventHandle.detach();
            this._frameValidEventHandle = null;
        }
    };
    /**
     * Callback function for handling Show Context Menu Events fired by PFDs.
     *
     * The callback function is given the gesture object, pickResults, and row index of the PFD node it was fired from
     * as arguments
     * The callback should return undefined.
     *
     * @param {function} value callback function to be called when showContextMenuEvent is handled.
     *    When this function is called it will receive an `Array.<PickResult>`,
     *    gesture object, and row index as parameters.
     */
    VisualizationManager.prototype.setShowContextMenuCallback = function (value) {
        this._showContextMenuCallback = value;
    };
    VisualizationManager.prototype.getTooltipDataOverrideCallback = function () {
        return this._tooltipDataOverride;
    };
    /**
     * Callback function for overriding the default data for tooltips.
     *
     * The callback function is given the pickResults and gesture object as an argument.
     * The callback should return Array.<CustomTooltipData> or CustomTooltipData.
     *
     * If undefined is returned from the callback then the default tips will display.
     *     If null or an empty array is returned then no tips will be displayed.
     *
     * @param {function} value callback function to be called on hover.
     *    When this function is called it will receive an `Array.<PickResult>`
     *    and gesture object as parameters.
     */
    VisualizationManager.prototype.setTooltipDataOverrideCallback = function (value) {
        this._tooltipDataOverride = value;
    };
    VisualizationManager.prototype.sizeVisualization = function () {
        // no need to check against a cached size
        // portable does an early return if the size hasn't changed
        if (this._baseView && this._viewAdded) {
            this._baseView.setFrame([0, 0, this._frameWidth, this._frameHeight]);
        }
    };
    VisualizationManager.prototype.setZoomOverlayStylesFromStyle = function (style) {
        if (style) {
            var wallStyle = style.getAttributeBundleForStyleElement(StyleElement.GRAPH_WALLS);
            var valueStyle = style.getAttributeBundleForStyleElement(StyleElement.GRAPH_VALUE_TEXT);
            var fontSize = valueStyle === null || valueStyle === void 0 ? void 0 : valueStyle.getStyleAttributeDimensionValue(StyleAttribute.FONT_SIZE, null);
            this._zoomOverlayStyleOverrides = {
                'background-color': (wallStyle === null || wallStyle === void 0 ? void 0 : wallStyle.getStyleAttributeUnsignedValue(StyleAttribute.FILL_COLOR, 0)) || 0,
                color: (valueStyle === null || valueStyle === void 0 ? void 0 : valueStyle.getStyleAttributeUnsignedValue(StyleAttribute.TEXT_COLOR, 16777215)) || 16777215,
                'font-family': (valueStyle === null || valueStyle === void 0 ? void 0 : valueStyle.getStyleAttributeStringValue(StyleAttribute.FONT_FAMILY, 'sans-serif')) || 'sans-serif',
                'font-size': fontSize ? fontSize.getScalarValueForUnit(DimensionUnit.POINTS, -1, 1) + 'pt' : '14pt',
            };
            this._applyZoomOverlayStyleOverrides();
        }
    };
    VisualizationManager.prototype._applyZoomOverlayStyleOverrides = function () {
        var element = this.getElement();
        if (element && this._zoomOverlayStyleOverrides) {
            // Part of this implementation will move to portable. For our temporary all-host
            // level implementation, we assume that if zoomOverlayStyleOverrides are provided
            // then we should use the scroll overlay
            if (!this._zoomOverlayMessageDiv) {
                var isMac = navigator.appVersion.indexOf('Mac') !== -1;
                var zoomMessage = ResourceManager.getLocalizedString('sas_commons', isMac ? 'zoom.cmd.txt' : 'zoom.ctrl.txt');
                this._zoomOverlayMessageDiv = document.createElement('div');
                var zoomMessageSpan = document.createElement('span');
                this._zoomOverlayMessageDiv.appendChild(zoomMessageSpan);
                zoomMessageSpan.textContent = zoomMessage;
                zoomMessageSpan.setAttribute('class', 'sasZoomOverlayMessage');
            }
            var wallBackgroundColor = this._zoomOverlayStyleOverrides['background-color'];
            this._zoomOverlayMessageDiv.setAttribute('class', 'sasZoomOverlayHidden');
            this._zoomOverlayMessageDiv.style.setProperty('--overlay-bg', getRgbaColorString(wallBackgroundColor, 0.75));
            // add this to the theme
            this._zoomOverlayMessageDiv.style.setProperty('--overlay-message-bg', getRgbaColorString(wallBackgroundColor, 1));
            this._zoomOverlayMessageDiv.style.setProperty('--overlay-color', ColorUtilities.getHexStringFromColor(this._zoomOverlayStyleOverrides['color']));
            this._zoomOverlayMessageDiv.style.setProperty('--font-family', this._zoomOverlayStyleOverrides['font-family']);
            this._zoomOverlayMessageDiv.style.setProperty('--font-size', this._zoomOverlayStyleOverrides['font-size']);
            element.appendChild(this._zoomOverlayMessageDiv);
        }
    };
    VisualizationManager.prototype._displayZoomOverlay = function () {
        var _this = this;
        var _a, _b;
        if (!this._restrictViewportGestures) {
            return false;
        }
        // supportedFunction is sort of a hack. We do not have access to GTL api in VisualizationManager
        // However, since the state of the models can easily change during execution we need to ask
        // GTL if the zoom overlay is supported every time we are asked to display the zoom overlay
        // we don't want to show it over a piechart, but someone could easily swap out the Graph's layout
        // for one that does support it later. We will have a model in the nova-graph and openui case, and
        // we ask that model if the zoom overlay is supported in addition to looking at _zoomOVerlayEnabled
        // For report based cases (va-sdk) we do not have a model and rely and completley rely on the
        // app to let us know if we should use the zoom overlay.
        var supportedFunction = (_a = this._model) === null || _a === void 0 ? void 0 : _a.isZoomOverlaySupported.bind(this._model);
        if (this._model && (!supportedFunction || !supportedFunction())) {
            return false;
        }
        if (this._zoomMessageTimerId > -1) {
            clearTimeout(this._zoomMessageTimerId);
        }
        (_b = this._zoomOverlayMessageDiv) === null || _b === void 0 ? void 0 : _b.setAttribute('class', 'sasZoomOverlayVisible');
        this._zoomMessageTimerId = setTimeout(function () {
            _this._removeZoomMessage();
        }, 1500);
        return true;
    };
    VisualizationManager.prototype._removeZoomMessage = function () {
        var _a;
        if (this._zoomMessageTimerId === -1) {
            return;
        }
        (_a = this._zoomOverlayMessageDiv) === null || _a === void 0 ? void 0 : _a.setAttribute('class', 'sasZoomOverlayHidden');
        clearTimeout(this._zoomMessageTimerId);
        this._zoomMessageTimerId = -1;
    };
    VisualizationManager.prototype._createStackedArcGISWebMapDiv = function () {
        var element = this.getElement();
        if (!this._stackedArcGISWebMapDiv) {
            this._stackedArcGISWebMapDiv = document.createElement('div');
            this._stackedArcGISWebMapDiv.style.setProperty('width', '100%');
            this._stackedArcGISWebMapDiv.style.setProperty('height', '100%');
        }
        if (element && !element.contains(this._stackedArcGISWebMapDiv)) {
            element.prepend(this._stackedArcGISWebMapDiv);
        }
        this._createStackedArcGISWebMap();
    };
    VisualizationManager.prototype._createStackedArcGISWebMap = function () {
        var _a;
        var canvasElement = _getCanvasElement((_a = this._visContainerId) !== null && _a !== void 0 ? _a : "");
        if (!this._model || !this._baseView || !canvasElement || !this._stackedArcGISWebMapDiv) {
            console.error("Cannot create stacked webmap without required inputs.");
            return;
        }
        var InjectedDisplay = getStackedWebmapDisplay();
        var display = (InjectedDisplay) ? new InjectedDisplay() : null;
        this.setStackedWebmapDisplay(display);
        if (!display) {
            console.error("Cannot create stacked webmap without supplemental display.");
            return;
        }
        display.create(this._model, this._baseView, canvasElement, this._stackedArcGISWebMapDiv);
    };
    VisualizationManager.prototype._destroyStackedWebmapDisplay = function () {
        var display = this.getStackedWebmapDisplay();
        if (display)
            display.destroy();
    };
    VisualizationManager.prototype._showStackedArcGISWebMap = function () {
        var _a;
        (_a = this._stackedArcGISWebMapDiv) === null || _a === void 0 ? void 0 : _a.style.setProperty('display', 'visible');
    };
    VisualizationManager.prototype._hideStackedArcGISWebMap = function () {
        var _a;
        (_a = this._stackedArcGISWebMapDiv) === null || _a === void 0 ? void 0 : _a.style.setProperty('display', 'hidden');
    };
    VisualizationManager.prototype.destroy = function () {
        var _a;
        if (this._visContainerId) {
            TooltipManager.instance().hideTooltips(this._visContainerId, false);
        }
        this.setStackedWebmapDisplay(null);
        (_a = this._stackedArcGISWebMapDiv) === null || _a === void 0 ? void 0 : _a.remove();
        this._stackedArcGISWebMapDiv = null;
        this._unobserveElement();
        this.setModel(null);
        this.setContainerId(null);
        this._gestureOverrides = null;
        this._accessibilityActionCallback = null;
        this._showContextMenuCallback = null;
    };
    VisualizationManager.prototype._handleFrameValid = function () {
        this._viewRendered = true;
        if (!this._gestureHelper) {
            this._initHelpersAndListeners();
        }
        if (!this._frameValidCallback) {
            this.setFrameValidCallback(null); //detach event, cleanup.
        }
        else {
            this._frameValidCallback();
        }
    };
    VisualizationManager.prototype._handleRenderingTypeChange = function () {
        var _a;
        if (((_a = this._baseView) === null || _a === void 0 ? void 0 : _a.getRenderingType()) === RenderingType.STACKED_ARCGIS_WEBMAP) {
            this._createStackedArcGISWebMapDiv();
            this._showStackedArcGISWebMap();
        }
        else {
            this._hideStackedArcGISWebMap();
        }
    };
    VisualizationManager.prototype._addViewToViewManager = function () {
        var view = this._baseView;
        var vis = this.getElement();
        if (view && vis && !this._viewAdded && this._visContainerId) {
            this._viewAdded = true;
            ViewManager.addView(view, this._visContainerId, this._renderMode);
            if (!this._frameValidEventHandle) {
                // tslint:disable-next-line:no-unbound-method
                this._frameValidEventHandle = view.attachEvent(BaseView.FRAME_VALID, this._handleFrameValid, this);
            }
            if (this._renderingTypeEventHandle)
                this._renderingTypeEventHandle.detach();
            this._renderingTypeEventHandle = view.attachEvent(BaseView.RENDERING_TYPE_CHANGE, this._handleRenderingTypeChange, this);
            addActiveVisualization(this);
            this._handleRenderingTypeChange();
        }
    };
    VisualizationManager.prototype._removeViewFromViewManager = function () {
        TooltipManager.instance().hideTooltips(this._visContainerId);
        ViewManager.removeView(this._baseView);
        removeActiveVisualization(this);
    };
    VisualizationManager.prototype._makeActiveView = function () {
        _activeViewId = this._visContainerId;
    };
    VisualizationManager.prototype._handleShowContextMenuEvent = function (event) {
        if (!this._showContextMenuCallback || !(event instanceof ShowContextMenuEvent)) {
            return;
        }
        var pickResult = event.getPickResult();
        if (!pickResult) {
            return;
        }
        var domRef = this.getElement();
        if (!domRef) {
            return;
        }
        var rowIndex = -1;
        var entries = pickResult.getEntries();
        if (entries) {
            //FIXME: Hmm. getIndices is on GTLPickResultEntry...
            var entry = entries[0];
            if (entry && entry.getIndices) {
                // tslint:disable-next-line:no-unsafe-any
                rowIndex = entry.getIndices()[0];
            }
        }
        if (rowIndex < 0) {
            return;
        }
        // tslint:disable-next-line:no-unsafe-any
        var preferredTargetLocation = pickResult.getPreferredTargetLocation();
        var gesture = GestureHelper.getPositionFromPoint(
        // tslint:disable-next-line:no-unsafe-any
        { x: preferredTargetLocation[0], y: preferredTargetLocation[1] }, domRef);
        // TODO(mikcol): doc comment on setShowContextMenuCallback suggests a different signature
        // TODO(mikcol)| for this callback.
        // tslint:disable-next-line:no-unsafe-any
        this._showContextMenuCallback(gesture, pickResult, rowIndex);
    };
    VisualizationManager.prototype._hideTooltips = function () {
        TooltipManager.instance().hideTooltips(this._visContainerId);
    };
    VisualizationManager.prototype._destroyHelpers = function () {
        var element = this.getElement();
        if (element) {
            // NOTE(mikcol): was not previously the bound onKeyUp instance in legacy
            // NOTE(cojhal): changed from keyup to keydown
            element.removeEventListener('keydown', this._onKeyDown);
            element.removeEventListener('blur', this._onBlur);
        }
        if (this._gestureHelper) {
            this._gestureHelper.destroy();
            this._gestureHelper = null;
        }
        if (this._narratorHelper) {
            this._narratorHelper.destroy();
            this._narratorHelper = null;
        }
        if (this._showContextMenuEventListener) {
            this._showContextMenuEventListener.detach();
            this._showContextMenuEventListener = null;
        }
        if (this._accessibilityFocusChangeListener) {
            this._accessibilityFocusChangeListener.detach();
            this._accessibilityFocusChangeListener = null;
        }
    };
    VisualizationManager.prototype._unobserveElement = function () {
        if (this._visContainerId && this._resizeObserver) {
            this._resizeObserver.disconnect();
        }
    };
    return VisualizationManager;
}());
export default VisualizationManager;
